<template>
  <v-container class="py-12">
    <v-row>
      <v-col :cols="12">
        <div class="title-container">
          <div class="title-block">
            <span class="page-title">Manutenções Corretivas</span>
            <v-breadcrumbs class="app-breadcrumb" divider="›" :items="breadcrumbs"></v-breadcrumbs>
          </div>
          <div class="button-group">
            <v-btn color="primary" elevation="0" to="/manutencoes/corretivas/novo">
              <v-icon left small>mdi-plus</v-icon>
              Nova corretiva
            </v-btn>
          </div>
        </div>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12">
        <v-card>
          <v-card-text>
            <v-data-table
              :headers="headers"
              :items="items"
              :loading="loading"
              :server-items-length="total"
              :items-per-page="pagination.limit"
              @update:page="onPage"
              @update:items-per-page="onLimit"
              :footer-props="footerProps"
              no-data-text="Nenhuma manutenção corretiva encontrada"
              loading-text="Carregando..."
              class="app-table hover-table"
            >
              <template v-slot:[`item.vehicle`]="{ item }">
                <div class="font-weight-medium">{{ item.plate || 'N/A' }}</div>
                <div class="caption grey--text">{{ item.brand }} {{ item.model }}</div>
              </template>
              <template v-slot:[`item.total_cost`]="{ item }">
                <span class="font-weight-medium">{{ formatCurrency(item.total_cost) }}</span>
              </template>
              <template v-slot:[`item.maintenance_type`]="{ item }">
                {{ maintenanceTypeLabel(item.maintenance_type) }}
              </template>
              <template v-slot:[`item.maintenance_status`]="{ item }">
                <v-chip x-small :color="getStatusColor(item.maintenance_status)" dark>
                  {{ statusLabel(item.maintenance_status) }}
                </v-chip>
              </template>
              <template v-slot:[`item.created_at`]="{ item }">
                {{ item.created_at | defaultDate('DD/MM/YYYY HH:mm') }}
              </template>
              <template v-slot:[`item.actions`]="{ item }">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon small color="primary" v-bind="attrs" v-on="on" @click="$router.push('/manutencoes/corretivas/' + item.id)">
                      <v-icon small>mdi-pencil-outline</v-icon>
                    </v-btn>
                  </template>
                  <span>Editar</span>
                </v-tooltip>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon small color="error" v-bind="attrs" v-on="on" @click="remove(item)">
                      <v-icon small>mdi-delete-outline</v-icon>
                    </v-btn>
                  </template>
                  <span>Remover</span>
                </v-tooltip>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import axios from 'axios'

const formatter = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' })

export default {
  name: 'CorrectiveList',
  data: () => ({
    breadcrumbs: [
      { text: 'Home', to: '/home', disabled: false, exact: true },
      { text: 'Manutenções', disabled: false, to: '/manutencoes', exact: true },
      { text: 'Corretivas', disabled: true, exact: true },
    ],
    headers: [
      { text: 'Veículo', value: 'vehicle' },
      { text: 'Fornecedor', value: 'supplier_name' },
      { text: 'Tipo', value: 'maintenance_type' },
      { text: 'Status', value: 'maintenance_status' },
      { text: 'Custo total', value: 'total_cost' },
      { text: 'Abertura', value: 'created_at' },
      { text: 'Ações', value: 'actions', sortable: false, width: '100px' },
    ],
    footerProps: {
      itemsPerPageText: 'Itens por página:',
      itemsPerPageOptions: [10, 20, 30, 40, 50],
      pageText: '{0}-{1} de {2}',
    },
    items: [],
    total: 0,
    loading: false,
    pagination: { page: 1, limit: 10 },
    resources: {
      maintenanceTypes: [],
      statuses: [],
    },
  }),
  created() {
    this.loadResources()
    this.load()
  },
  methods: {
    toNum(v) {
      const n = Number(v)
      return Number.isFinite(n) ? n : 0
    },
    formatCurrency(value) {
      return formatter.format(this.toNum(value))
    },
    getStatusColor(status) {
      const map = {
        draft: 'grey',
        open: 'blue',
        in_progress: 'orange',
        awaiting_parts: 'purple',
        awaiting_approval: 'amber darken-2',
        completed: 'green',
        cancelled: 'red',
      }
      return map[status] || 'grey'
    },
    maintenanceTypeLabel(value) {
      const item = (this.resources.maintenanceTypes || []).find((t) => t.value === value)
      return item?.label || value
    },
    statusLabel(value) {
      const item = (this.resources.statuses || []).find((s) => s.value === value)
      return item?.label || value
    },
    notifyError(message) {
      if (this.$dialog?.notify) {
        this.$dialog.notify.error(message, { position: 'top-right', timeout: 5000 })
      }
    },
    notifySuccess(message) {
      if (this.$dialog?.notify) {
        this.$dialog.notify.success(message, { position: 'top-right', timeout: 4000 })
      }
    },
    async loadResources() {
      try {
        const { data } = await axios.get('/api/v2/maintenance/corrective/resources')
        const content = data?.content || {}
        this.resources.maintenanceTypes = content.maintenanceTypes || []
        this.resources.statuses = content.statuses || []
      } catch (error) {
        this.notifyError(this.$codes ? this.$codes(error) : 'Erro ao carregar recursos.')
      }
    },
    async load() {
      this.loading = true
      try {
        const { data } = await axios.get('/api/v2/maintenance/corrective', {
          params: { page: this.pagination.page, limit: this.pagination.limit },
        })
        const content = data?.content || {}
        this.items = content.data || []
        this.total = content.total || 0
      } catch (error) {
        this.notifyError(this.$codes ? this.$codes(error) : 'Erro ao carregar corretivas.')
      } finally {
        this.loading = false
      }
    },
    onPage(page) {
      this.pagination.page = page
      this.load()
    },
    onLimit(limit) {
      this.pagination.limit = limit
      this.pagination.page = 1
      this.load()
    },
    async remove(item) {
      const proceedAction = await this.$dialog.confirm({
        text: 'Deseja remover esta manutenção corretiva? As peças vinculadas retornarão ao estoque.',
        title: 'Remover corretiva',
        actions: { false: 'Cancelar', true: 'Remover' },
      })

      if (!proceedAction) return

      this.loading = true

      try {
        await axios.delete(`/api/v2/maintenance/corrective/${item.id}`)
        this.notifySuccess('Manutenção corretiva removida com sucesso.')
        await this.load()
      } catch (error) {
        this.notifyError(this.$codes ? this.$codes(error) : 'Erro ao remover corretiva.')
      } finally {
        this.loading = false
      }
    },
  },
}
</script>
